import type { State, StateCreator, StoreMutatorIdentifier } from 'zustand';
import type { QueryStringSerializer } from '@/scopes/common/infrastructure/query-strings/queryStringSerializer';
import type { BrowserQueryString } from '@/scopes/common/infrastructure/query-strings/types';
import { createBrowserQueryStringKeepingParams } from '@/scopes/common/infrastructure/query-strings/browserQueryStringKeepingParams';

export interface QueryParametersToStateMapper<S extends State, P> {
  toState: (queryParameters: P) => S;
  toParameters: (state: S) => P;
}

interface Dependencies<S extends State, P> {
  queryStringSerializer: QueryStringSerializer<P>;
  browserQueryString: BrowserQueryString;
  queryParametersToStateMapper: QueryParametersToStateMapper<S, P>;
}

type SyncQueryString = <
  T extends State,
  Mps extends [StoreMutatorIdentifier, unknown][] = [],
  Mcs extends [StoreMutatorIdentifier, unknown][] = [],
>(
  f: StateCreator<T, Mps, Mcs>
) => StateCreator<T, Mps, Mcs>;

const createIdentityQueryParametersToStateMapper = <S extends State, P>() => ({
  toState: (queryParameters: P) => queryParameters as unknown as S,
  toParameters: (state: S) => state as unknown as P,
});

export const createClientQueryStringZustandMiddleware = <S extends State, P>(
  dependencies: Partial<Dependencies<S, P>>
) => {
  const {
    browserQueryString = createBrowserQueryStringKeepingParams({}),
    queryParametersToStateMapper = createIdentityQueryParametersToStateMapper(),
    queryStringSerializer,
  } = dependencies as Dependencies<S, P>;

  function getInitialState() {
    const queryString = browserQueryString.get();

    if (!queryString) return {};

    const queryParameters = queryStringSerializer.deserialize(queryString);
    return queryParametersToStateMapper.toState(queryParameters);
  }

  return ((f: StateCreator<S>): StateCreator<S> =>
    (set, get, store) => {
      const initialState = getInitialState();
      const setState = store.setState;

      const updateQueryString = (state: S) => {
        const queryParameters = queryParametersToStateMapper.toParameters(state);
        const queryString = queryStringSerializer.serialize(queryParameters);
        browserQueryString.set(queryString);
      };

      const setMiddleware: typeof set = (...a) => {
        set(...a);
        updateQueryString(get());
      };

      store.setState = (...a) => {
        setState(...a);
        updateQueryString(store.getState());
      };

      return Object.assign({}, f(setMiddleware, get, store), initialState);
    }) as SyncQueryString;
};
